import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { useApplySelectionTarget, useIsRouteTargetCurrent } from '../../../lib/services/selection-target.service';
import { useQueueOpen } from '../../../lib/stores/connectivity.store';
import {
  decideSelectionTargetAction,
  readRouteSelectionTarget,
  readSelectionTarget,
  selectionLocationKey,
  stripSelectionTarget,
} from '../../../lib/helpers/selection-target.helpers';
import { IActiveSelection, SelectionTargetAction } from '../../../lib/types/selection-target.types';
import { OUTCOME_SNACKBAR_MS } from '../../../lib/snackbar.constants';
import { useIntlSnackbar } from '../../../lib/hooks/use-intl-snackbar';
import { useIsMounted } from '../../../lib/hooks/use-is-mounted';

// Components
import LoadingPage from '../../_pages/_boilerplate/LoadingPage/loading-page.container';
import { messages } from './selection-target-gate.messages';
import { REFUSAL_MESSAGES, SELECTION_TARGET_HOLD_MS } from './selection-target-gate.constants';

interface ISelectionTargetGateProps {
  children: React.ReactNode;
}

// Holds the staff app on a loading page while a targeted link switches the user's selection.
// See CLAUDE.md → "Targeted selection links".
export const SelectionTargetGate: React.FC<ISelectionTargetGateProps> = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const user = getAuthUser();
  const isOnline = useQueueOpen();
  const notify = useIntlSnackbar();
  const applySelectionTarget = useApplySelectionTarget();
  const isRouteTargetCurrent = useIsRouteTargetCurrent();
  const isMounted = useIsMounted();
  const [applyingKey, setApplyingKey] = useState<string | null>(null);
  const [handledKey, setHandledKey] = useState<string | null>(null);
  const [releasedKey, setReleasedKey] = useState<string | null>(null);
  // Warn once per location while offline, so re-renders don't stack snackbars.
  const offlineNoticeKey = useRef<string | null>(null);

  const locationKey = selectionLocationKey(location);
  const queryTarget = useMemo(() => readSelectionTarget(location.search), [location.search]);
  const routeTarget = useMemo(() => readRouteSelectionTarget(location.pathname), [location.pathname]);
  const selection: IActiveSelection | undefined = user?.settings;
  const routeTargetIsCurrent = useMemo(
    () => !!routeTarget && isRouteTargetCurrent(routeTarget, selection),
    [routeTarget, selection, isRouteTargetCurrent],
  );
  const action = decideSelectionTargetAction({
    isSignedIn: !!user,
    isHandled: handledKey === locationKey,
    isOnline,
    queryTarget,
    routeTarget,
    isRouteTargetCurrent: routeTargetIsCurrent,
  });

  // Removes a handled location's targeting params and marks it done — unless the user has already moved on.
  const settleLocation = useCallback((key: string) => {
    if (selectionLocationKey(history.location) !== key) return;
    if (readSelectionTarget(history.location.search)) {
      history.replace({ ...history.location, search: stripSelectionTarget(history.location.search) });
    }
    if (isMounted()) setHandledKey(selectionLocationKey(history.location));
  }, [history, isMounted]);

  useEffect(() => {
    if (applyingKey !== null) return;
    if (action === SelectionTargetAction.SETTLE) {
      settleLocation(locationKey);
      return;
    }
    if (action === SelectionTargetAction.SKIP_OFFLINE) {
      // Keep the params rather than settling: they are the only record of the link's intent, and
      // stripping them sends the user back to the email. The page still renders — only APPLY holds
      // it — and when the queue opens `action` recomputes to APPLY and the switch runs.
      if (offlineNoticeKey.current !== locationKey) {
        offlineNoticeKey.current = locationKey;
        notify(messages.offline, 'warning');
      }
      return;
    }
    if (action !== SelectionTargetAction.APPLY) return;

    setApplyingKey(locationKey);
    const linkLocation = history.location;
    let granted = false;
    let released = false;
    let movedByUser = false;
    let stopWatching: () => void = () => undefined;
    const holdTimer = window.setTimeout(() => {
      if (!isMounted()) return;
      released = true;
      // Once released, a push or pop is the user's own move; a replace is a redirect the unfinished switch caused.
      stopWatching = history.listen((_location, historyAction) => {
        if (historyAction !== 'REPLACE') movedByUser = true;
      });
      setReleasedKey(locationKey);
      notify(messages.slow, 'info', OUTCOME_SNACKBAR_MS);
    }, SELECTION_TARGET_HOLD_MS);

    applySelectionTarget({ ...routeTarget, ...queryTarget })
      .then((result) => {
        granted = result.granted;
        if (!result.granted) {
          // A refusal reason this client doesn't know yet still gets a message rather than an empty snackbar.
          notify((result.reason && REFUSAL_MESSAGES[result.reason]) || messages.failed, 'warning');
        } else if (result.skippedIdCount) {
          notify(messages.skipped, 'info', OUTCOME_SNACKBAR_MS);
        }
      })
      .catch((error) => {
        console.error('applySelectionTarget failed:', error);
        notify(messages.failed, 'error');
      })
      .finally(() => {
        window.clearTimeout(holdTimer);
        stopWatching();
        const redirectedAway = released && !movedByUser && selectionLocationKey(history.location) !== locationKey;
        if (granted && redirectedAway && isMounted()) {
          // Replacing mints a new location key, so the returned location is marked handled directly.
          history.replace({ ...linkLocation, search: stripSelectionTarget(linkLocation.search) });
          setHandledKey(selectionLocationKey(history.location));
        } else {
          settleLocation(locationKey);
        }
        if (!isMounted()) return;
        setApplyingKey(null);
        setReleasedKey(null);
      });
  }, [action, applyingKey, locationKey, queryTarget, routeTarget, applySelectionTarget, notify, settleLocation, isMounted, history]);

  // A released switch holds no page; another location that needs one still waits for it to finish.
  const isHeldBySwitch = applyingKey !== null && releasedKey !== applyingKey;
  const isWaitingToApply = action === SelectionTargetAction.APPLY && releasedKey !== locationKey;
  if (isHeldBySwitch || isWaitingToApply) return <LoadingPage />;
  return <>{children}</>;
};
